import { c as _c } from "react/compiler-runtime";
import { getHostedIdentifySessionRequirementsOptions, patchHostedIdentifySessionVaultMutation } from '@onefootprint/fetch-hosted';
import type { VaultData } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { CollectEmail } from '@/idv2/features/identify/collect-email';
import { isSessionExpired } from '@/idv2/features/identify/handle-session-expired';
import { pushIdentifyBack } from '@/idv2/features/identify/identify-back';
import { logIdentifyError } from '@/idv2/features/identify/log-identify-error';
import { useTenantLogo } from '@/idv2/features/identify/use-tenant-logo';
import { useVaultErrorMessage } from '@/idv2/features/identify/use-vault-error-message';
const emailSearchSchema = z.object({
  email: z.string().optional()
});
type EmailLoaderData = {
  displayTermsAndConditions: boolean;
};
export const Route = createFileRoute('/identify/email')({
  validateSearch: emailSearchSchema,
  loader: async ({
    context
  }): Promise<EmailLoaderData> => {
    const headers = {
      'X-Fp-Authorization': context.onboardingSessionToken
    };
    const data = await context.queryClient.ensureQueryData(getHostedIdentifySessionRequirementsOptions({
      headers
    }));
    return {
      displayTermsAndConditions: data?.displayTermsAndConditions ?? false
    };
  },
  component: EmailPage
});
function EmailPage() {
  const $ = _c(12);
  const tenantLogo = useTenantLogo();
  const navigate = useNavigate();
  const {
    onboardingSessionToken
  } = Route.useRouteContext();
  const {
    email
  } = Route.useSearch();
  const {
    displayTermsAndConditions
  } = Route.useLoaderData();
  const getVaultErrorMessage = useVaultErrorMessage();
  let t0;
  if ($[0] !== onboardingSessionToken) {
    t0 = patchHostedIdentifySessionVaultMutation({
      headers: {
        "X-Fp-Authorization": onboardingSessionToken
      }
    });
    $[0] = onboardingSessionToken;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const vaultMutation = useMutation(t0);
  let t1;
  if ($[2] !== getVaultErrorMessage || $[3] !== navigate || $[4] !== vaultMutation) {
    t1 = async (data, onError) => {
      ;
      try {
        await vaultMutation.mutateAsync({
          body: data
        });
        const submittedEmail = data["id.email"] as string;
        pushIdentifyBack({
          to: "/identify/email",
          search: {
            email: submittedEmail
          }
        });
        navigate({
          to: "/identify",
          search: {
            email: submittedEmail
          }
        });
      } catch (t2) {
        const error = t2;
        if (isSessionExpired(error)) {
          throw error;
        }
        logIdentifyError("collect-email", error);
        const message = getVaultErrorMessage(error, "id.email");
        if (message) {
          onError(message);
        }
      }
    };
    $[2] = getVaultErrorMessage;
    $[3] = navigate;
    $[4] = vaultMutation;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleSubmit = t1;
  let t2;
  if ($[6] !== displayTermsAndConditions || $[7] !== email || $[8] !== handleSubmit || $[9] !== tenantLogo || $[10] !== vaultMutation.isPending) {
    t2 = <CollectEmail headerIcon={tenantLogo} onSubmit={handleSubmit} isLoading={vaultMutation.isPending} defaultEmail={email} showLegalFooter={displayTermsAndConditions} />;
    $[6] = displayTermsAndConditions;
    $[7] = email;
    $[8] = handleSubmit;
    $[9] = tenantLogo;
    $[10] = vaultMutation.isPending;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}